import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent
} from '@angular/material/autocomplete';
import {
    finalize,
    map,
    Observable,
    of,
    startWith,
    distinctUntilChanged,
    catchError
} from 'rxjs';
import { PlantsService } from '../services/plants.service';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export type MeteringPoint = {
    meteringPointId: string;
    meterType: string;
    isMain: boolean;
};

type MpRowForm = {
    meteringPointId: FormControl<string>;
    meterType: FormControl<string>;
    isMain: FormControl<boolean>;
    meterTypeSearch: FormControl<string>;
};

export type InverterFormModel = {
    id: FormControl<number | null>;
    brand: FormControl<string>;
    model: FormControl<string>;
    serialNumber: FormControl<string>;
    maxProductionCapacity: FormControl<string>;
    totalBatteryCapacityKwhsolarPanels: FormControl<string>;
    batteryChargePowerRateKw: FormControl<string>;
    totalPanelPowerKwh: FormControl<string>;
    plantId: FormControl<number>;
    meteringPointIds: FormControl<MeteringPoint[]>;
    solarFactorThreshold: FormControl<string>;
    meteringPoints: FormArray<FormGroup<MpRowForm>>;
};

type InverterDtoLike = any;

@Component({
    selector: 'app-update-inverter',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatAutocompleteModule,
        MatCardModule,
        MatCheckboxModule,
        TranslatePipe
    ],
    templateUrl: './update-inverter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./update-inverter.component.scss']
})
export class UpdateInverterComponent implements OnInit {
    plantId!: number;
    loading = true;
    saving = false;
    inverters: InverterDtoLike[] = [];
    selectedInverter?: InverterDtoLike;

    private brandToModels = new Map<string, string[]>();
    private readonly solarThresholdMin = 0;
    private readonly solarThresholdMax = 1;

    brandOptions: string[] = [];
    modelOptions: string[] = [];
    meterTypeOptions: { title: string; description?: string }[] = [];

    brandSearchCtrl = new FormControl<string>('', { nonNullable: true });
    modelSearchCtrl = new FormControl<string>('', { nonNullable: true });

    filteredBrands$: Observable<string[]> = of([]);
    filteredModels$: Observable<string[]> = of([]);

    invertersForm: FormGroup<InverterFormModel> = this.fb.group<
        InverterFormModel
    >({
        id: this.fb.control<number | null>(null),
        brand: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        model: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        serialNumber: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        maxProductionCapacity: this.fb.control('', { nonNullable: true }),
        totalBatteryCapacityKwhsolarPanels: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        batteryChargePowerRateKw: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        totalPanelPowerKwh: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        plantId: this.fb.control(0, { nonNullable: true }),
        meteringPointIds: this.fb.control<MeteringPoint[]>([], {
            nonNullable: true
        }),
        solarFactorThreshold: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required, this.solarThresholdValidator()]
        }),
        meteringPoints: this.fb.array<FormGroup<MpRowForm>>([])
    });

    constructor(
        private fb: FormBuilder,
        private plantsSvc: PlantsService,
        private route: ActivatedRoute,
        private router: Router,
        private sb: MatSnackBar,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        const plantIdStr = this.route.snapshot.paramMap.get('plantId');
        this.plantId = Number(plantIdStr);

        if (!this.plantId) {
            this.notify(this.t('invalid_plant_id_in_route'), 'error');
            this.router.navigate(['/plants/all-plant-list']);
            return;
        }

        this.invertersForm.controls.plantId.setValue(this.plantId);

        const inverterIdQP = this.route.snapshot.queryParamMap.get(
            'inverterId'
        );
        const serialQP = this.route.snapshot.queryParamMap.get('serialNumber');
        const inverterIdToPick = inverterIdQP ? Number(inverterIdQP) : null;
        const serialToPick = serialQP ? String(serialQP) : null;

        this.loadPrerequisitesThenData(inverterIdToPick, serialToPick);
        this.hookBrandAndSearchSync();
    }

    get meteringPointsFA(): FormArray<FormGroup<MpRowForm>> {
        return this.invertersForm.controls.meteringPoints;
    }

    private t(key: string, params?: Record<string, unknown>): string {
        return this.translate.instant(key, params);
    }

    private solarThresholdValidator(
        min = this.solarThresholdMin,
        max = this.solarThresholdMax
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const raw = String(control.value ?? '').trim();

            if (!raw) {
                return null;
            }

            const normalized = raw.replace(',', '.');
            const parsed = Number(normalized);

            if (Number.isNaN(parsed)) {
                return { solarThresholdInvalid: true };
            }

            if (parsed < min || parsed > max) {
                return {
                    solarThresholdOutOfRange: {
                        min,
                        max,
                        actual: parsed
                    }
                };
            }

            return null;
        };
    }

    private normalizeSolarFactorThreshold(value: any): string {
        if (Array.isArray(value)) {
            return String(value[0] ?? '')
                .trim()
                .replace(',', '.');
        }

        return String(value ?? '')
            .trim()
            .replace(',', '.');
    }

    private getFriendlyErrorMessage(
        err: any,
        context:
            | 'prerequisites'
            | 'load-plant'
            | 'update'
            | 'general' = 'general'
    ): string {
        const raw =
            String(err?.statusMessage ?? '') ||
            String(err?.message ?? '') ||
            String(err?.error?.message ?? '') ||
            this.t('something_went_wrong');

        const message = raw.toLowerCase();

        if (
            message.includes('solarfactorthreshold') &&
            message.includes('out of range')
        ) {
            return this.t('solar_threshold_out_of_range', {
                min: this.solarThresholdMin,
                max: this.solarThresholdMax
            });
        }

        if (
            message.includes('solarfactorthreshold') &&
            message.includes('not valid')
        ) {
            return this.t('solar_threshold_invalid_numeric_value');
        }

        if (
            message.includes('exactly one metering point') ||
            message.includes('maincountinvalid')
        ) {
            return this.t('exactly_one_metering_point_main');
        }

        if (
            message.includes('invalid plantid') ||
            message.includes('plantid not set')
        ) {
            return this.t('plant_id_missing_or_invalid');
        }

        if (message.includes('inverter id not found')) {
            return this.t('inverter_id_could_not_be_resolved');
        }

        if (message.includes('invalidargument')) {
            if (context === 'update') {
                return this.t('inverter_update_contains_invalid_values');
            }

            if (context === 'prerequisites') {
                return this.t('failed_load_prerequisites');
            }

            if (context === 'load-plant') {
                return this.t('failed_load_plant_inverter_data');
            }

            return this.t('some_submitted_values_invalid');
        }

        return raw;
    }

    private handleError(
        err: any,
        context:
            | 'prerequisites'
            | 'load-plant'
            | 'update'
            | 'general' = 'general'
    ): void {
        console.error(`[update-inverter:${context}]`, err);
        this.notify(this.getFriendlyErrorMessage(err, context), 'error');
    }

    private validateSolarThresholdBeforeSubmit(): boolean {
        const ctrl = this.invertersForm.controls.solarFactorThreshold;

        ctrl.updateValueAndValidity({ onlySelf: true });

        if (ctrl.hasError('required')) {
            ctrl.markAsTouched();
            this.notify(this.t('solar_threshold_required'), 'error');
            return false;
        }

        if (ctrl.hasError('solarThresholdInvalid')) {
            ctrl.markAsTouched();
            this.notify(this.t('solar_threshold_invalid'), 'error');
            return false;
        }

        if (ctrl.hasError('solarThresholdOutOfRange')) {
            ctrl.markAsTouched();
            this.notify(
                this.t('solar_threshold_out_of_range', {
                    min: this.solarThresholdMin,
                    max: this.solarThresholdMax
                }),
                'error'
            );
            return false;
        }

        return true;
    }

    private loadPrerequisitesThenData(
        inverterIdToPick: number | null,
        serialToPick: string | null
    ) {
        this.loading = true;

        this.plantsSvc
            .getAddInverterPrerequisites()
            .pipe(
                catchError((err: any) => {
                    this.loading = false;
                    this.handleError(err, 'prerequisites');
                    return of(null);
                })
            )
            .subscribe((res: any) => {
                if (!res) return;

                this.applyPrerequisites(res);

                this.plantsSvc
                    .getPlantFullDetail(this.plantId)
                    .pipe(
                        map((x: any) => ({
                            inverters: Array.isArray(x?.inverters)
                                ? x.inverters
                                : []
                        })),
                        catchError((err: any) => {
                            this.loading = false;
                            this.handleError(err, 'load-plant');
                            return of({ inverters: [] });
                        })
                    )
                    .subscribe(({ inverters }) => {
                        this.inverters = inverters ?? [];

                        const picked =
                            (inverterIdToPick
                                ? this.inverters.find(
                                      inv =>
                                          Number(inv?.id) === inverterIdToPick
                                  )
                                : undefined) ??
                            (serialToPick
                                ? this.inverters.find(
                                      inv =>
                                          String(
                                              inv?.serialNumber ??
                                                  inv?._serialNumber
                                          ) === serialToPick
                                  )
                                : undefined) ??
                            this.inverters[0];

                        if (!picked) {
                            this.ensureAtLeastOneMpRow();
                            this.loading = false;
                            return;
                        }

                        this.selectInverter(picked);
                        this.loading = false;
                    });
            });
    }

    private applyPrerequisites(res: any) {
        this.brandToModels.clear();

        const brands = Array.isArray(res?.brands) ? res.brands : [];
        this.brandOptions = brands.map((b: any) => b?.title).filter(Boolean);

        brands.forEach((b: any) => {
            const title = b?.title ?? '';
            const modelsArr = Array.isArray(b?.models) ? b.models : [];
            const modelTitles = modelsArr
                .map((m: any) => m?.title)
                .filter(Boolean);
            this.brandToModels.set(title, modelTitles);
        });

        this.meterTypeOptions = (res?.meterTypes ?? []).map((t: any) => ({
            title: t.title,
            description: t.description
        }));

        this.filteredBrands$ = this.createFilter$(
            this.brandSearchCtrl,
            () => this.brandOptions
        );
        this.filteredModels$ = this.createFilter$(
            this.modelSearchCtrl,
            () => this.modelOptions
        );
    }

    selectInverter(inv: InverterDtoLike) {
        this.selectedInverter = inv;

        const id = Number(inv?.id ?? 0) || null;
        const brand = String(inv?.brand ?? inv?._brand ?? '');
        let model = String(inv?.model ?? inv?._model ?? '');
        const serial = String(inv?.serialNumber ?? inv?._serialNumber ?? '');

        const solarFactorThresholdRaw =
            inv?.solarFactorThreshold ?? inv?._solarFactorThreshold ?? '';

        const solarFactorThreshold = this.normalizeSolarFactorThreshold(
            solarFactorThresholdRaw
        );

        const maxProd = String(
            inv?.maxProductionCapacity ?? inv?._maxProductionCapacity ?? ''
        );
        const battCap = String(
            inv?.totalBatteryCapacityKwh ?? inv?._totalBatteryCapacityKwh ?? ''
        );
        const panelPow = String(
            inv?.totalPanelPowerKwh ?? inv?._totalPanelPowerKwh ?? ''
        );
        const battRate = String(
            inv?.batteryChargePowerRateKw ??
                inv?._batteryChargePowerRateKw ??
                ''
        );

        if (model.toLowerCase() === 'none') {
            model = '';
        }

        this.modelOptions = this.brandToModels.get(brand) ?? [];

        this.invertersForm.patchValue(
            {
                id,
                brand,
                model,
                serialNumber: serial,
                maxProductionCapacity: maxProd,
                totalBatteryCapacityKwhsolarPanels: battCap,
                totalPanelPowerKwh: panelPow,
                batteryChargePowerRateKw: battRate,
                solarFactorThreshold,
                plantId: this.plantId
            },
            { emitEvent: false }
        );

        this.brandSearchCtrl.setValue(brand, { emitEvent: false });
        this.modelSearchCtrl.setValue(model, { emitEvent: false });

        const mps: any[] =
            inv?.meteringPoints ??
            inv?._meteringPointIds ??
            inv?.meteringPointIds ??
            [];

        this.meteringPointsFA.clear();

        if (Array.isArray(mps) && mps.length) {
            mps.forEach(mp => {
                this.meteringPointsFA.push(
                    this.createMeteringPointRow({
                        meteringPointId:
                            mp?.meteringPointId ?? mp?._meteringPointId ?? '',
                        meterType: mp?.meterType ?? mp?._meterType ?? '',
                        isMain: !!(mp?.isMain ?? mp?._isMain)
                    })
                );
            });
        } else {
            this.ensureAtLeastOneMpRow();
        }

        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity({ emitEvent: false });
    }

    addMeteringPoint() {
        const isFirst = this.meteringPointsFA.length === 0;

        const row = this.fb.group<MpRowForm>({
            meteringPointId: this.fb.control('', {
                nonNullable: true,
                validators: [Validators.required]
            }),
            meterType: this.fb.control('', {
                nonNullable: true,
                validators: [Validators.required]
            }),
            isMain: this.fb.control(isFirst, { nonNullable: true }),
            meterTypeSearch: this.fb.control('', { nonNullable: true })
        });

        row.controls.meterTypeSearch.valueChanges.subscribe((v: string) => {
            row.controls.meterType.setValue(v ?? '', { emitEvent: false });
            row.controls.meterType.updateValueAndValidity({ onlySelf: true });
            this.syncMeteringPointsControl();
            this.invertersForm.updateValueAndValidity();
        });

        this.meteringPointsFA.push(row);
        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity();
    }

    removeMeteringPoint(i: number) {
        this.meteringPointsFA.removeAt(i);

        if (this.meteringPointsFA.length === 0) {
            this.addMeteringPoint();
        }

        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity();
    }

    setAsMain(i: number) {
        this.meteringPointsFA.controls.forEach((g, idx) => {
            g.controls.isMain.setValue(idx === i);
        });

        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity();
    }

    onMeterTypeSelected(i: number, ev: MatAutocompleteSelectedEvent) {
        const val = ev.option.value as any;
        const title = typeof val === 'string' ? val : val?.title ?? '';
        const row = this.meteringPointsFA.at(i);

        row.controls.meterTypeSearch.setValue(title, { emitEvent: false });
        row.controls.meterType.setValue(title);
        row.controls.meterType.updateValueAndValidity();
        this.syncMeteringPointsControl();
    }

    getMeterTypeFiltered(
        i: number
    ): Observable<{ title: string; description?: string }[]> {
        const row = this.meteringPointsFA.at(i);
        const ctrl = row.controls.meterTypeSearch ?? row.controls.meterType;

        return ctrl.valueChanges.pipe(
            startWith(ctrl.value ?? ''),
            map(v => String(v ?? '').toLowerCase()),
            map(q =>
                this.meterTypeOptions.filter(
                    mt =>
                        mt.title.toLowerCase().includes(q) ||
                        (mt.description ?? '').toLowerCase().includes(q)
                )
            )
        );
    }

    private createMeteringPointRow(mp?: Partial<MeteringPoint>) {
        const row = this.fb.group<MpRowForm>({
            meteringPointId: this.fb.control(mp?.meteringPointId ?? '', {
                nonNullable: true,
                validators: [Validators.required]
            }),
            meterType: this.fb.control(mp?.meterType ?? '', {
                nonNullable: true,
                validators: [Validators.required]
            }),
            isMain: this.fb.control(!!mp?.isMain, { nonNullable: true }),
            meterTypeSearch: this.fb.control(mp?.meterType ?? '', {
                nonNullable: true
            })
        });

        row.controls.meterTypeSearch.valueChanges.subscribe((v: string) => {
            row.controls.meterType.setValue(v ?? '', { emitEvent: false });
            row.controls.meterType.updateValueAndValidity({ onlySelf: true });
            this.syncMeteringPointsControl();
            this.invertersForm.updateValueAndValidity();
        });

        return row;
    }

    private ensureAtLeastOneMpRow() {
        if (this.meteringPointsFA.length === 0) {
            this.addMeteringPoint();
        }
    }

    private syncMeteringPointsControl() {
        const arr: MeteringPoint[] = this.meteringPointsFA.controls.map(g => ({
            meteringPointId: g.controls.meteringPointId.value,
            meterType: g.controls.meterType.value,
            isMain: g.controls.isMain.value
        }));

        this.invertersForm.controls.meteringPointIds.setValue(arr);
    }

    private hookBrandAndSearchSync() {
        this.brandSearchCtrl.valueChanges.subscribe(v => {
            this.invertersForm.controls.brand.setValue(v ?? '', {
                emitEvent: false
            });
            this.invertersForm.controls.brand.updateValueAndValidity({
                onlySelf: true
            });
        });

        this.modelSearchCtrl.valueChanges.subscribe(v => {
            this.invertersForm.controls.model.setValue(v ?? '', {
                emitEvent: false
            });
            this.invertersForm.controls.model.updateValueAndValidity({
                onlySelf: true
            });
        });

        this.invertersForm.controls.brand.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe((brand: string) =>
                this.onBrandChanged((brand ?? '').trim(), true)
            );
    }

    onBrandSelected(ev: MatAutocompleteSelectedEvent) {
        const val = String(ev.option.value ?? '');
        this.invertersForm.controls.brand.setValue(val, { emitEvent: true });
        this.invertersForm.controls.brand.updateValueAndValidity();
        this.onBrandChanged(val.trim(), true);
    }

    private onBrandChanged(brand: string, clearModel = true) {
        if (clearModel) {
            this.invertersForm.controls.model.setValue('', {
                emitEvent: false
            });
            this.modelSearchCtrl.setValue('', { emitEvent: false });
        }

        this.modelOptions = this.brandToModels.get(brand) ?? [];
        this.modelSearchCtrl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: true
        });
    }

    private createFilter$(
        ctrl: FormControl<string>,
        getOptions: () => string[]
    ): Observable<string[]> {
        return ctrl.valueChanges.pipe(
            startWith(ctrl.value ?? ''),
            map(v => (v ?? '').toLowerCase()),
            map(q => getOptions().filter(x => x.toLowerCase().includes(q)))
        );
    }

    submitUpdate() {
        if (!this.plantId) {
            this.notify(this.t('plant_id_not_set_update_inverter'), 'error');
            return;
        }

        if (!this.validateSolarThresholdBeforeSubmit()) {
            return;
        }

        if (this.invertersForm.invalid) {
            this.invertersForm.markAllAsTouched();
            this.notify(this.t('please_fix_highlighted_fields'), 'error');
            return;
        }

        this.syncMeteringPointsControl();

        const mains = (this.invertersForm.value.meteringPointIds ?? []).filter(
            mp => !!mp.isMain
        );
        if (mains.length !== 1) {
            this.invertersForm.setErrors({ mainCountInvalid: true });
            this.notify(this.t('exactly_one_metering_point_main'), 'error');
            return;
        }

        const raw = this.invertersForm.getRawValue();
        const inverterId = raw.id ?? this.selectedInverter?.id;

        if (!inverterId) {
            this.notify(this.t('inverter_id_not_found_for_update'), 'error');
            return;
        }

        const payload = {
            plantId: this.plantId,
            id: Number(inverterId),
            serialNumber: String(raw.serialNumber ?? '').trim(),
            brand: String(raw.brand ?? '').trim(),
            model: String(raw.model ?? '').trim(),
            maxProductionCapacity: String(
                raw.maxProductionCapacity ?? '0'
            ).trim(),
            totalBatteryCapacityKwh: String(
                raw.totalBatteryCapacityKwhsolarPanels ?? '0'
            ).trim(),
            totalPanelPowerKwh: String(raw.totalPanelPowerKwh ?? '0').trim(),
            batteryChargePowerRateKw: String(
                raw.batteryChargePowerRateKw ?? '0'
            ).trim(),
            solarFactorThreshold: this.normalizeSolarFactorThreshold(
                raw.solarFactorThreshold
            ),
            meteringPointIds: (raw.meteringPointIds ?? []).map(mp => ({
                meteringPointId: String(mp.meteringPointId ?? '').trim(),
                meterType: String(mp.meterType ?? '').trim(),
                isMain: !!mp.isMain
            }))
        };

        this.saving = true;

        this.plantsSvc
            .updatePlantInverter(payload)
            .pipe(finalize(() => (this.saving = false)))
            .subscribe({
                next: () => {
                    this.notify(
                        this.t('inverter_updated_successfully'),
                        'success'
                    );
                    this.router.navigate([
                        '/plants/plants-detail',
                        this.plantId
                    ]);
                },
                error: (err: any) => {
                    this.handleError(err, 'update');
                }
            });
    }

    onCancel() {
        this.router.navigate(['/plants/plants-detail', this.plantId]);
    }

    private notify(
        msg: string,
        type: 'success' | 'error' | 'warn' | 'info' = 'info',
        duration = 5000
    ) {
        const cls =
            type === 'success'
                ? 'snack-success'
                : type === 'error'
                ? 'snack-error'
                : type === 'warn'
                ? 'snack-warn'
                : 'snack-info';

        this.sb.open(msg, this.t('close'), {
            duration,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: [cls]
        });
    }

    goPlantsDetails() {
        this.router.navigate(['plants/plants-detail', this.plantId]);
    }
}
